// GH #1723. The funder context behind one comparison row.
//
// A reviewer asked to say whether an engine got a match wrong needs the funder's
// own criteria and exclusions in front of them. Fetched on open rather than shipped
// with the grid: 185 opportunity profiles is a multi-megabyte payload for the
// handful anyone actually reads.
import { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Typography,
} from '@mui/material'
import { useNotification } from '@refinedev/core'

import { dataProvider } from '../providers/dataProvider'
import { OpportunityContext } from '../interfaces/matchRunInterfaces'

const Section = ({
  title,
  body,
}: {
  title: string
  body: string | null | undefined
}) =>
  body ? (
    <Box mb={2}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {body}
      </Typography>
    </Box>
  ) : null

const amountLabel = (opportunity: OpportunityContext) => {
  if (opportunity.no_min_max_amount) return 'no stated min/max'
  const bounds = [opportunity.min_amount, opportunity.max_amount]
    .filter((v) => v !== null && v !== undefined)
    .map((v) => `$${Number(v).toLocaleString()}`)
  if (bounds.length) return bounds.join(' – ')
  return opportunity.amount || null
}

export default function OpportunityDialog({
  opportunityUuid,
  open,
  onClose,
}: {
  opportunityUuid: string | null
  open: boolean
  onClose: () => void
}) {
  const notify = useNotification()
  const [opportunity, setOpportunity] = useState<OpportunityContext | null>(
    null
  )
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !opportunityUuid) return
    let cancelled = false
    setLoading(true)
    setOpportunity(null)
    dataProvider.custom!({
      url: `/match-runs/opportunity/${opportunityUuid}`,
      method: 'get',
    })
      .then((response) => {
        if (!cancelled) {
          setOpportunity(response.data as unknown as OpportunityContext)
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify?.open?.({
            type: 'error',
            message: 'Could not load that opportunity',
          })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, opportunityUuid])

  const amount = opportunity ? amountLabel(opportunity) : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {opportunity?.fund_name || 'Opportunity'}
        <Typography variant="subtitle2" color="text.secondary">
          {opportunity?.funder_name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : null}

        {opportunity ? (
          <>
            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
              {amount ? <Chip size="small" label={amount} /> : null}
              {opportunity.always_open ? (
                <Chip size="small" label="always open" />
              ) : null}
              {opportunity.max_rank_is_4 ? (
                <Chip size="small" color="warning" label="capped at rank 4" />
              ) : null}
              {opportunity.only_funds_registered_charities ? (
                <Chip size="small" label="registered charities only" />
              ) : null}
              {opportunity.gambling_proceeds ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label="gambling proceeds"
                />
              ) : null}
              {opportunity.is_international ? (
                <Chip size="small" variant="outlined" label="international" />
              ) : null}
              {(opportunity.tags || []).map((tag) => (
                <Chip key={tag} size="small" variant="outlined" label={tag} />
              ))}
            </Box>

            {opportunity.application_link ? (
              <Box mb={2}>
                <Link
                  href={opportunity.application_link}
                  target="_blank"
                  rel="noreferrer"
                  variant="body2"
                >
                  {opportunity.application_link}
                </Link>
              </Box>
            ) : null}

            <Section title="Description" body={opportunity.description} />
            <Section title="Criteria" body={opportunity.criteria} />
            <Section title="Exclusions" body={opportunity.exclusions} />
            <Section title="Timing" body={opportunity.timing} />

            <Divider sx={{ my: 2 }} />
            <Section title="Funder" body={opportunity.funder_description} />
            <Section
              title="Profile text — what the model actually reads"
              body={opportunity.profile_text}
            />
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
